import { push } from "connected-react-router";
import { put, takeEvery, takeLatest } from "redux-saga/effects";

import { handleApiErrors } from "../../../api/helpers/handleApiErrors";
import { toastForApiErrors } from "../../../api/helpers/toastForApiErrors";
import { portalApi } from "../../../api/rtkApi";
import { showMessageToast } from "../../../components/toasts/MessageToast";
import { callApi } from "../../../sagas/helpers/api";
import { setError, setSuccess, startLoading } from "../../../store/appSlice";
import * as AccountsApi from "../api/Accounts";
import {
  accountsOperationPaths,
  addItem,
  createAccount,
  fetchAccount,
  FetchAccountPayload,
  fetchAccounts,
  setItem,
  setList,
  updateAccount,
} from ".";

import type { PayloadAction } from "@reduxjs/toolkit";
import type { List } from "../../../api/helpers/pagination";
import type { UUID } from "../../../utils";
import type { ObjectWithId } from "../../../utils/ObjectWithId";
import type { Account } from "../models/Account";
import type { AccountPost } from "../models/AccountPost";

function* fetchAccountsSaga({ payload }: PayloadAction<FetchAccountPayload>) {
  const { customerId, environment } = payload ?? {};
  const { listing: operationPath } = accountsOperationPaths();

  yield put(startLoading(operationPath));
  try {
    const data: List<Account> = yield callApi(AccountsApi.getAccounts, {
      customerId,
      environment,
    });

    yield put(setList(data));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* fetchAccountSaga({ payload }: PayloadAction<ObjectWithId>) {
  const { id } = payload ?? {};

  const { details: operationPath } = accountsOperationPaths(id);

  yield put(startLoading(operationPath));

  try {
    const item: Account = yield callApi(AccountsApi.getAccount, id);

    yield put(
      setItem({
        id,
        item,
      })
    );
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* createAccountSaga({ payload }: PayloadAction<AccountPost>) {
  const { create: operationPath } = accountsOperationPaths();

  yield put(startLoading(operationPath));
  try {
    const account: Account = yield callApi(AccountsApi.postAccount, payload);

    yield put(addItem(account));
    yield put(portalApi.util.invalidateTags(["Accounts"]));
    yield put(setSuccess(operationPath));

    if (payload.additionalCustomers?.length) {
      showMessageToast({
        bodyContent: "Bank accounts were updated on selected merchants",
        variant: "success",
      });
    }

    yield put(push(`/configuration/accounts/${account.id}`));
  } catch (error) {
    const errors = handleApiErrors(error);

    if (errors) {
      toastForApiErrors(errors);
    }

    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* updateAccountSaga({
  payload,
}: PayloadAction<{ id: UUID; data: Account }>) {
  const { data, id } = payload;

  const { update: operationPath } = accountsOperationPaths(id);

  yield put(startLoading(operationPath));
  try {
    const account: Account = yield callApi(AccountsApi.patchAccount, id, data);

    yield put(setItem({ id, item: account }));
    yield put(portalApi.util.invalidateTags(["Accounts"]));
    yield put(setSuccess(operationPath));
    yield put(push(`/configuration/accounts/${account.id}`));
  } catch (error) {
    const errors = handleApiErrors(error);

    if (errors) {
      toastForApiErrors(errors);
    }

    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

export default function* rootSaga() {
  yield takeLatest(fetchAccounts.type, fetchAccountsSaga);
  yield takeLatest(fetchAccount.type, fetchAccountSaga);
  yield takeEvery(createAccount.type, createAccountSaga);
  yield takeEvery(updateAccount.type, updateAccountSaga);
}
